import { PropsWithChildren, ReactNode } from 'react';

import {
  Modal as ChakraModal,
  ModalBody,
  ModalCloseButton,
  ModalContent as ChakraModalContent,
  ModalFooter as ChakraModalFooter,
  ModalFooterProps,
  ModalHeader,
  ModalOverlay,
  ModalProps as ChakraModalProps,
  HStack,
} from '@chakra-ui/react';

import { BoxProps } from './reexport';

export interface ModalContentProps {
  header?: ReactNode;
  subheader?: ReactNode;
  body: ReactNode;
  width?: BoxProps['width'];
  footer?: ReactNode;
  footerStyle?: ModalFooterProps;
  noCloseButton?: boolean;
}

interface ModalWrapperProps extends ChakraModalProps {
  h?: BoxProps['h'];
  w?: BoxProps['w'];
  top?: BoxProps['top'];
  transparentOverlay?: boolean;
  trapFocus?: boolean;
  border?: BoxProps['border'];
  borderRadius?: BoxProps['borderRadius'];
  borderLeft?: BoxProps['borderLeft'];
  borderLeftColor?: BoxProps['borderLeftColor'];
}

interface ModalProps
  extends Omit<ModalWrapperProps, 'children'>,
    ModalContentProps {}

export const ModalFooterWrapper = ({ children }: PropsWithChildren) => (
  <HStack
    bgColor='grey.offWhite'
    px={4}
    h='40px'
    flexGrow={1}
    gap='2px'
    align='center'
    borderTopWidth='2px'
    borderTopColor='grey.border'
    fontWeight='regular'
    fontSize='sm'
  >
    {children}
  </HStack>
);

export const ModalContent = ({
  header,
  subheader,
  body,
  footer,
  footerStyle,
  width = '100%',
  noCloseButton = false,
}: ModalContentProps) => {
  const defaultFooterStyle = {
    minH: '73px',
    borderBottomRadius: '8px',
    overflow: 'hidden',
  } as ModalFooterProps;
  const footerProps = { ...defaultFooterStyle, ...footerStyle };

  return (
    <>
      {header ? (
        <ModalHeader
          borderBottom='none'
          minWidth={width}
          width={width}
          display='flex'
          flexDirection='column'
          padding='0'
        >
          <HStack
            align='center'
            justify='space-between'
            w='100%'
            p='16px'
            pb='8px'
          >
            {header}
            {noCloseButton ? null : (
              <ModalCloseButton
                width='24px'
                height='24px'
                top='0'
                right='0'
                borderRadius='4px'
                size='sm'
                position='relative'
              />
            )}
          </HStack>
          {subheader}
        </ModalHeader>
      ) : null}
      <ModalBody p='0' minWidth={width} width={width}>
        {body}
      </ModalBody>
      {footer ? (
        <ChakraModalFooter minWidth={width} width={width} {...footerProps}>
          {footer}
        </ChakraModalFooter>
      ) : null}
    </>
  );
};

export const ModalWrapper = ({
  isOpen,
  onClose,
  h,
  w,
  top,
  children,
  isCentered,
  transparentOverlay = false,
  trapFocus = true,
  border,
  borderRadius,
  borderLeft,
  borderLeftColor,
}: ModalWrapperProps) => {
  return (
    <ChakraModal
      isOpen={isOpen}
      onClose={onClose}
      isCentered={isCentered}
      trapFocus={trapFocus}
    >
      <ModalOverlay
        bg={transparentOverlay ? 'rgba(0,0,0,0)' : 'rgba(36, 45, 60, 0.9)'}
      />

      <ChakraModalContent
        h={h}
        w={w}
        m='0'
        boxShadow='0px 4px 8px -2px #1018281A'
        borderColor='grey.border'
        border={border ?? '1px solid'}
        borderRadius={borderRadius}
        borderLeft={borderLeft}
        borderLeftColor={borderLeftColor}
        bgColor='grey.white'
        top={top ?? '40px'}
      >
        {children}
      </ChakraModalContent>
    </ChakraModal>
  );
};

export const Modal = ({
  isOpen,
  onClose,
  h,
  transparentOverlay,
  isCentered,
  border,
  borderRadius,
  top,
  ...contentProps
}: ModalProps) => {
  return (
    <ModalWrapper
      transparentOverlay={transparentOverlay}
      isOpen={isOpen}
      onClose={onClose}
      isCentered={isCentered}
      border={border}
      borderRadius={borderRadius}
      top={top}
      h={h}
    >
      <ModalContent {...contentProps} />
    </ModalWrapper>
  );
};
